/**
 * ct-currency.js — display-only USD / EUR conversion.
 *
 * WHY THIS EXISTS AS THEME CODE INSTEAD OF SHOPIFY MARKETS
 * -------------------------------------------------------
 * Shopify's native "sell in local currencies" requires Shopify Payments (or Adyen)
 * as the primary gateway. Shopify Payments does not operate in Chile, and this store
 * checks out through other gateways, so `enabled_presentment_currencies` is locked to
 * ['CLP'] and the Markets currency toggle is unavailable. The only way to show a
 * foreign price is to rewrite it in the browser.
 *
 * That makes this layer PURELY COSMETIC. Cart, checkout, orders, email receipts and
 * refunds are all still CLP. Every surface that shows a converted price therefore has
 * to say so — see ensureNotices(). Do not "clean up" those notices: showing a US$
 * figure and then charging pesos without warning is the failure mode this guards.
 *
 * HOW IT WORKS
 * ------------
 * Prices are printed by 137 Liquid `| money` call sites across 20 files, and the PDP
 * additionally rewrites them from JS on variant change. Rather than touch each one,
 * this walks text nodes, wraps anything matching the CLP money format in a marked
 * span that remembers the original peso amount, and re-renders those spans whenever
 * the shopper switches currency. A MutationObserver re-runs the walk for the cart
 * drawer, Section Rendering API swaps and the PDP's own textContent writes.
 */
(function () {
  'use strict';

  var root = document.getElementById('ct-currency-root');
  if (!root) return;

  var BASE = 'CLP';
  var HOME_COUNTRY = 'CL';
  var CUR_KEY = 'ct-currency';
  var RATE_KEY = 'ct-currency-rates';
  var GEO_KEY = 'ct-currency-geo';
  var RATE_TTL = 12 * 60 * 60 * 1000; // 12h — rates move slowly, don't hammer the API
  var GEO_TTL = 24 * 60 * 60 * 1000;  // a visitor's country rarely changes mid-day
  var GEO_TIMEOUT = 1500;             // past this, reveal the picker rather than stall

  // Euro is the sensible default here; everywhere else outside Chile gets USD.
  // Includes the non-EU users of the euro (Monaco, Montenegro, Kosovo et al).
  var EUROZONE = {
    AD: 1, AT: 1, BE: 1, CY: 1, DE: 1, EE: 1, ES: 1, FI: 1, FR: 1, GR: 1,
    HR: 1, IE: 1, IT: 1, LT: 1, LU: 1, LV: 1, MC: 1, ME: 1, MT: 1, NL: 1,
    PT: 1, SI: 1, SK: 1, SM: 1, VA: 1, XK: 1
  };

  /**
   * Baked-in last resort, verified 2026-08-21 against Banco Central de Chile
   * (mindicador.cl) and cross-checked against the ECB EUR/USD via frankfurter.
   * Only used when BOTH live sources fail. Refresh occasionally so a long outage
   * doesn't quote a year-old rate. Values are CLP per 1 unit of foreign currency.
   *
   * Deliberately NOT sourced from open.er-api.com / exchangerate-api.com: on
   * 2026-08-21 both quoted 817 CLP/USD against an actual 923, an 11% error, while
   * their EUR/USD cross was correct. Their CLP feed is not trustworthy.
   */
  var FALLBACK = { USD: 923.23, EUR: 1077.66 };

  var META = {
    CLP: { label: 'CLP', name: 'Peso chileno', prefix: '$', decimals: 0, locale: 'es-CL' },
    USD: { label: 'USD', name: 'Dólar estadounidense', prefix: 'US$', decimals: 2, locale: 'en-US' },
    EUR: { label: 'EUR', name: 'Euro', prefix: '€', decimals: 2, locale: 'en-US' }
  };

  var enabled = root.getAttribute('data-enabled') !== 'false';
  if (!enabled) return;

  // Shopper-facing buffer. The shopper is charged in CLP and their own bank applies
  // an FX spread on top, so the raw mid-market rate systematically under-quotes what
  // they actually pay. A small uplift makes the displayed figure land closer to the
  // real card statement. Configurable in Theme settings > Monedas.
  var buffer = parseFloat(root.getAttribute('data-buffer'));
  if (!isFinite(buffer) || buffer < 0 || buffer > 20) buffer = 0;
  buffer = buffer / 100;

  var geoEnabled = root.getAttribute('data-geo') !== 'false';

  var offered = (root.getAttribute('data-currencies') || 'USD,EUR')
    .split(',')
    .map(function (c) { return c.trim().toUpperCase(); })
    .filter(function (c) { return META[c] && c !== BASE; });

  if (!offered.length) return;

  var all = [BASE].concat(offered);

  var state = {
    code: BASE,
    rates: null
  };

  /* ---------------------------------------------------------------- storage */

  function readStored(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function writeStored(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }

  /* ------------------------------------------------------------------ rates */

  /**
   * mindicador.cl is the Banco Central de Chile's published reference rate, which is
   * the right primary for a Chilean store: it is authoritative locally and updates
   * daily. jsdelivr's currency-api is the cross-check/fallback; the two agreed to
   * within 0.1% at build time.
   */
  function fetchOfficial() {
    return fetch('https://mindicador.cl/api', { mode: 'cors' })
      .then(function (r) { if (!r.ok) throw new Error('mindicador ' + r.status); return r.json(); })
      .then(function (d) {
        var out = {};
        if (d.dolar && d.dolar.valor > 0) out.USD = d.dolar.valor;
        if (d.euro && d.euro.valor > 0) out.EUR = d.euro.valor;
        if (!out.USD && !out.EUR) throw new Error('mindicador: no usable rates');
        return out;
      });
  }

  function fetchFallback() {
    return fetch('https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/clp.json', { mode: 'cors' })
      .then(function (r) { if (!r.ok) throw new Error('jsdelivr ' + r.status); return r.json(); })
      .then(function (d) {
        var clp = d.clp || {};
        var out = {};
        // This feed is quoted as foreign-per-CLP; invert to CLP-per-foreign.
        if (clp.usd > 0) out.USD = 1 / clp.usd;
        if (clp.eur > 0) out.EUR = 1 / clp.eur;
        if (!out.USD && !out.EUR) throw new Error('jsdelivr: no usable rates');
        return out;
      });
  }

  /**
   * Guards against a source that returns a technically-valid but absurd number.
   * A 10x error here would silently misprice the whole catalogue, so anything
   * outside a wide sanity band is discarded in favour of the next source.
   */
  function plausible(rates) {
    if (!rates) return false;
    var ok = false;
    for (var code in rates) {
      if (!META[code]) continue;
      var v = rates[code];
      if (!isFinite(v) || v < 200 || v > 5000) return false;
      ok = true;
    }
    return ok;
  }

  function cachedRates() {
    var raw = readStored(RATE_KEY);
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.at || (Date.now() - parsed.at) > RATE_TTL) return null;
      return plausible(parsed.rates) ? parsed.rates : null;
    } catch (e) { return null; }
  }

  function loadRates() {
    var cached = cachedRates();
    if (cached) {
      state.rates = cached;
      return Promise.resolve(cached);
    }

    return fetchOfficial()
      .catch(function () { return fetchFallback(); })
      .then(function (rates) {
        if (!plausible(rates)) throw new Error('implausible rates');
        writeStored(RATE_KEY, JSON.stringify({ at: Date.now(), rates: rates }));
        state.rates = rates;
        return rates;
      })
      .catch(function () {
        // Both live sources unusable — quote the pinned rate rather than nothing.
        state.rates = FALLBACK;
        return FALLBACK;
      });
  }

  /* ------------------------------------------------------------- geolocation */

  /**
   * Shopify resolves the visitor's country from their IP server side and exposes it
   * at /browsing_context_suggestions.json — the endpoint its own Geolocation app uses.
   * Same-origin, no API key, no third-party processor handling visitor IPs.
   *
   * This MUST run client side. Storefront pages are served from CDN cache, so gating
   * the picker in Liquid would bake one visitor's country into a page served to
   * everyone else.
   *
   * FAILS OPEN: any error, timeout or malformed response resolves to null, which
   * reveals the picker in CLP. A Chilean seeing a spare dropdown costs nothing; an
   * international shopper who can't find USD defeats the entire feature.
   */
  function withTimeout(promise, ms) {
    return new Promise(function (resolve) {
      var settled = false;
      var timer = setTimeout(function () {
        if (!settled) { settled = true; resolve(null); }
      }, ms);
      promise.then(
        function (v) { if (!settled) { settled = true; clearTimeout(timer); resolve(v); } },
        function () { if (!settled) { settled = true; clearTimeout(timer); resolve(null); } }
      );
    });
  }

  function cachedGeo() {
    var raw = readStored(GEO_KEY);
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.at || (Date.now() - parsed.at) > GEO_TTL) return null;
      return typeof parsed.country === 'string' ? parsed.country : null;
    } catch (e) { return null; }
  }

  function fetchGeo() {
    return fetch('/browsing_context_suggestions.json', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (r) { if (!r.ok) throw new Error('geo ' + r.status); return r.json(); })
      .then(function (d) {
        var detected = d && d.detected_values && d.detected_values.country;
        var code = detected && detected.handle;
        if (!code || !/^[A-Za-z]{2}$/.test(code)) throw new Error('geo: no country');
        return code.toUpperCase();
      });
  }

  function resolveGeo() {
    if (!geoEnabled) return Promise.resolve(null);

    var cached = cachedGeo();
    if (cached) return Promise.resolve(cached);

    return withTimeout(fetchGeo(), GEO_TIMEOUT).then(function (country) {
      if (country) writeStored(GEO_KEY, JSON.stringify({ at: Date.now(), country: country }));
      return country;
    });
  }

  function defaultFor(country) {
    if (!country || country === HOME_COUNTRY) return BASE;
    if (EUROZONE[country] && offered.indexOf('EUR') !== -1) return 'EUR';
    return offered.indexOf('USD') !== -1 ? 'USD' : BASE;
  }

  function reveal() {
    document.documentElement.setAttribute('data-ct-cur-ready', '1');
  }

  /**
   * `chosen` means this visitor picked a currency themselves on a previous visit.
   * An explicit choice always wins over geolocation — including for a Chilean who
   * deliberately switched to USD, who keeps both their currency and the picker.
   */
  function applyAudience(country, chosen) {
    if (country === HOME_COUNTRY && !chosen) {
      // Local shopper: no picker, and never anything but pesos.
      if (state.code !== BASE) setCurrency(BASE, false);
      return;
    }

    reveal();

    if (!chosen) {
      var want = defaultFor(country);
      // Not persisted: a geo guess shouldn't harden into an "explicit choice" that
      // outlives the trip, and shouldn't strand a mis-located visitor in USD.
      if (want !== state.code) setCurrency(want, false);
    }
  }

  /* --------------------------------------------------------------- rendering */

  /**
   * The money pattern is BUILT FROM `shop.money_format`, never hardcoded.
   *
   * This store runs `${{amount_no_decimals}}`, which groups with a COMMA — `$95,990`.
   * A pattern pinned to period grouping matched only the leading `95`, converted that,
   * and left `,990` sitting there as plain text, so the whole catalogue rendered as
   * `US$0.10,990` instead of `US$106.05`. The separator is a merchant setting that can
   * change in Admin > Settings > Store details at any time, so read it, don't assume it.
   *
   * Shopify's eight placeholders and what they emit for 1234567.89:
   *   amount                                   1,234,567.89
   *   amount_no_decimals                       1,234,568
   *   amount_with_comma_separator              1.234.567,89
   *   amount_no_decimals_with_comma_separator  1.234.568
   *   amount_with_apostrophe_separator         1'234'567.89
   *   amount_with_space_separator              1 234 567,89
   *   amount_no_decimals_with_space_separator  1 234 568
   *   amount_with_period_and_space_separator   1 234 567.89
   */
  var MONEY_SHAPES = {
    amount:                                  { group: ',', decimal: '.', decimals: 2 },
    amount_no_decimals:                      { group: ',', decimal: '',  decimals: 0 },
    amount_with_comma_separator:             { group: '.', decimal: ',', decimals: 2 },
    amount_no_decimals_with_comma_separator: { group: '.', decimal: '',  decimals: 0 },
    amount_with_apostrophe_separator:        { group: "'", decimal: '.', decimals: 2 },
    amount_with_space_separator:             { group: ' ', decimal: ',', decimals: 2 },
    amount_no_decimals_with_space_separator: { group: ' ', decimal: '',  decimals: 0 },
    amount_with_period_and_space_separator:  { group: ' ', decimal: '.', decimals: 2 }
  };

  function escRe(s) {
    return s.replace(/[.*+?^${}()|[\]\\\-]/g, '\\$&');
  }

  /**
   * Pulls the separators, decimal count and literal prefix/suffix out of a Liquid
   * money format. Falls back to the Chilean period-grouped shape when the attribute
   * is missing or unrecognised — a cached page from before this shipped still has to
   * render something sane rather than mangle every price.
   */
  function parseMoneyFormat(fmt) {
    var placeholder = /\{\{\s*([a-z_]+)\s*\}\}/.exec(fmt || '');
    var shape = (placeholder && MONEY_SHAPES[placeholder[1]]) ||
                MONEY_SHAPES.amount_no_decimals_with_comma_separator;
    var literals = placeholder ? (fmt || '').split(placeholder[0]) : [];
    return {
      group: shape.group,
      decimal: shape.decimal,
      decimals: shape.decimals,
      prefix: literals.length ? literals[0] : '$',
      suffix: literals.length > 1 ? literals[1] : ''
    };
  }

  var FMT = parseMoneyFormat(root.getAttribute('data-money-format'));

  /**
   * Separators accepted when READING a price. Deliberately wider than the store's own
   * setting: a handful of prices in this theme are hardcoded strings in section
   * settings and quiz data rather than `| money` output, so period-grouped `$229.990`
   * still shows up on pages whose live format is comma-grouped. Reading both costs
   * nothing and leaves no price behind.
   *
   * Space is only accepted when the store actually groups with one — `$50 000` is a
   * price under that format, but under any other it is far more likely to be prose.
   * The decimal separator is excluded so `$1.234` can't be read as grouping when the
   * format's own decimal mark is a period.
   */
  var READ_SEPS = ['.', ',', "'"];
  if (FMT.group === ' ') READ_SEPS = READ_SEPS.concat([' ', ' ', ' ']);
  READ_SEPS = READ_SEPS.filter(function (s) { return !FMT.decimals || s !== FMT.decimal; });

  // The grouped branch is tried first so `$95,990` reads as 95990, not 95.
  var MONEY_RE = new RegExp(
    escRe(FMT.prefix.trim() || '$') + '\\s?' +
    '(\\d{1,3}(?:[' + READ_SEPS.map(escRe).join('') + ']\\d{3})+|\\d+)' +
    (FMT.decimals ? '(?:' + escRe(FMT.decimal) + '(\\d{1,2}))?' : '') +
    '(\\s*CLP)?',
    'g'
  );

  // Capture groups shift when the format carries decimals; name them once.
  var DEC_GROUP = FMT.decimals ? 2 : null;
  var CODE_GROUP = FMT.decimals ? 3 : 2;

  var STRIP_SEPS = new RegExp('[' + READ_SEPS.map(escRe).join('') + ']', 'g');

  function parseAmount(intPart, frac) {
    var n = parseFloat(intPart.replace(STRIP_SEPS, '') + (frac ? '.' + frac : ''));
    return isFinite(n) ? n : null;
  }

  // Round-trips a price back into the store's own format, so switching away from a
  // foreign currency and back restores the exact string Liquid printed.
  function formatCLP(clp, withCode) {
    var fixed = Math.abs(clp).toFixed(FMT.decimals);
    var parts = fixed.split('.');
    var s = FMT.prefix +
      parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, FMT.group) +
      (FMT.decimals ? FMT.decimal + parts[1] : '') +
      FMT.suffix;
    return withCode ? s + ' CLP' : s;
  }

  function render(clp, withCode) {
    if (state.code === BASE) return formatCLP(clp, withCode);
    var meta = META[state.code];
    var rate = state.rates && state.rates[state.code];
    if (!meta || !rate) return formatCLP(clp, withCode);

    var value = (clp / rate) * (1 + buffer);
    return meta.prefix + value.toLocaleString(meta.locale, {
      minimumFractionDigits: meta.decimals,
      maximumFractionDigits: meta.decimals
    });
  }

  /* ------------------------------------------------------------ DOM walking */

  var SKIP_TAGS = {
    SCRIPT: 1, STYLE: 1, TEXTAREA: 1, NOSCRIPT: 1, SELECT: 1, OPTION: 1,
    CODE: 1, PRE: 1, TITLE: 1, svg: 1, SVG: 1
  };

  // Set while we mutate the DOM ourselves, so the observer ignores our own writes.
  var writing = false;

  function skippable(el) {
    while (el && el.nodeType === 1) {
      if (SKIP_TAGS[el.tagName]) return true;
      if (el.hasAttribute('data-ct-cur')) return true;      // already converted
      if (el.hasAttribute('data-ct-cur-skip')) return true; // opted out (the picker)
      if (el.isContentEditable) return true;
      el = el.parentElement;
    }
    return false;
  }

  function convertTextNode(node) {
    var text = node.nodeValue;
    if (!text || text.indexOf('$') === -1) return;

    MONEY_RE.lastIndex = 0;
    if (!MONEY_RE.test(text)) return;
    MONEY_RE.lastIndex = 0;

    var frag = document.createDocumentFragment();
    var last = 0;
    var m;

    while ((m = MONEY_RE.exec(text)) !== null) {
      var clp = parseAmount(m[1], DEC_GROUP ? m[DEC_GROUP] : '');
      if (clp === null) continue;

      if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));

      var withCode = !!m[CODE_GROUP];
      var span = document.createElement('span');
      span.setAttribute('data-ct-cur', String(clp));
      if (withCode) span.setAttribute('data-ct-cur-code', '1'); // came from money_with_currency
      span.textContent = render(clp, withCode);
      frag.appendChild(span);

      last = m.index + m[0].length;
    }

    if (!frag.childNodes.length) return;
    if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
    if (node.parentNode) node.parentNode.replaceChild(frag, node);
  }

  function walk(target) {
    if (!target) return;
    if (target.nodeType === 3) {
      if (!skippable(target.parentElement)) convertTextNode(target);
      return;
    }
    if (target.nodeType !== 1) return;
    if (skippable(target)) return;

    var walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (!node.nodeValue || node.nodeValue.indexOf('$') === -1) return NodeFilter.FILTER_REJECT;
        return skippable(node.parentElement) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
      }
    });

    var found = [];
    var n;
    while ((n = walker.nextNode()) !== null) found.push(n);
    found.forEach(convertTextNode);
  }

  function repaint() {
    var nodes = document.querySelectorAll('[data-ct-cur]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var clp = parseFloat(el.getAttribute('data-ct-cur'));
      if (isFinite(clp)) el.textContent = render(clp, el.hasAttribute('data-ct-cur-code'));
    }
  }

  function scan(target) {
    writing = true;
    try { walk(target || document.body); } finally { writing = false; }
  }

  /* ----------------------------------------------------------- disclosure */

  var NOTE_TEXT = 'Precio de referencia. El cobro se realiza en pesos chilenos (CLP).';
  var NOTE_TEXT_LONG = 'Los precios en otras monedas son una referencia calculada al tipo de cambio del día. ' +
    'El pago se procesa en pesos chilenos (CLP) y el monto final lo determina tu banco.';

  /**
   * Anchors where a converted price sits next to a money decision.
   *
   * The note is inserted as a SIBLING of the anchor, never appended inside it.
   * Both `.cart__ctas` and `.pprice` are flex rows, so a `<p>` child becomes a flex
   * item: it lands beside the checkout button and squeezes "Total estimado" down to
   * one letter per line. Insert outside the flex container and the layout holds.
   *
   * `.cart__ctas` covers the cart page and the cart drawer (both render it), so one
   * entry reaches every checkout entry point without doubling up on the cart page.
   */
  var NOTE_ANCHORS = [
    { key: 'cart', sel: '.cart__ctas', position: 'before', text: NOTE_TEXT_LONG },
    { key: 'pdp', sel: '.pprice', position: 'after', text: NOTE_TEXT }
  ];

  function findNote(parent, key) {
    var found = parent.querySelectorAll(':scope > .ct-cur-note');
    for (var i = 0; i < found.length; i++) {
      if (found[i].getAttribute('data-note') === key) return found[i];
    }
    return null;
  }

  function ensureNotices() {
    var show = state.code !== BASE;

    NOTE_ANCHORS.forEach(function (anchor) {
      var hosts = document.querySelectorAll(anchor.sel);

      for (var i = 0; i < hosts.length; i++) {
        var el = hosts[i];
        var parent = el.parentElement;
        if (!parent) continue;

        var note = findNote(parent, anchor.key);
        if (!note) {
          if (!show) continue;
          note = document.createElement('p');
          note.className = 'ct-cur-note';
          note.setAttribute('data-note', anchor.key);
          note.setAttribute('data-ct-cur-skip', '');
          note.textContent = anchor.text;
          if (anchor.position === 'before') parent.insertBefore(note, el);
          else parent.insertBefore(note, el.nextSibling);
        }
        note.hidden = !show;
      }
    });
  }

  /* -------------------------------------------------------------- selector */

  function buildSelector() {
    var select = document.createElement('select');
    select.className = 'ct-cur-select';
    select.id = 'ctCurrencySelect';
    select.setAttribute('aria-label', 'Moneda de referencia');

    all.forEach(function (code) {
      var opt = document.createElement('option');
      opt.value = code;
      opt.textContent = META[code].label + ' ' + META[code].prefix;
      select.appendChild(opt);
    });

    select.value = state.code;
    select.addEventListener('change', function () {
      setCurrency(select.value);
    });

    return select;
  }

  function mountSelectors() {
    var hosts = document.querySelectorAll('[data-ct-cur-mount]');
    for (var i = 0; i < hosts.length; i++) {
      if (hosts[i].querySelector('.ct-cur-select')) continue;
      hosts[i].appendChild(buildSelector());
    }
  }

  function syncSelectors() {
    var sels = document.querySelectorAll('.ct-cur-select');
    for (var i = 0; i < sels.length; i++) {
      if (sels[i].value !== state.code) sels[i].value = state.code;
    }
  }

  /* ----------------------------------------------------------------- wiring */

  function setCurrency(code, persist) {
    if (!META[code] || all.indexOf(code) === -1) return;
    state.code = code;
    if (persist !== false) writeStored(CUR_KEY, code);

    writing = true;
    try {
      repaint();
      ensureNotices();
      syncSelectors();
      document.documentElement.setAttribute('data-ct-currency', code);
    } finally {
      writing = false;
    }
  }

  var pending = [];
  var scheduled = false;

  function flush() {
    scheduled = false;
    var batch = pending;
    pending = [];
    writing = true;
    try {
      batch.forEach(function (node) {
        if (node && (node.nodeType === 1 || node.nodeType === 3) && node.isConnected !== false) walk(node);
      });
      ensureNotices();
      mountSelectors();
      syncSelectors();
    } finally {
      writing = false;
    }
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(flush);
  }

  function observe() {
    if (typeof MutationObserver !== 'function') return;

    var observer = new MutationObserver(function (mutations) {
      if (writing) return;
      var dirty = false;

      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'childList') {
          for (var j = 0; j < m.addedNodes.length; j++) {
            var n = m.addedNodes[j];
            if (n.nodeType === 1 || n.nodeType === 3) { pending.push(n); dirty = true; }
          }
        } else if (m.type === 'characterData') {
          if (m.target && !skippable(m.target.parentElement)) { pending.push(m.target); dirty = true; }
        }
      }

      if (dirty) schedule();
    });

    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  function init() {
    var stored = readStored(CUR_KEY);
    var chosen = !!(stored && all.indexOf(stored) !== -1);
    if (chosen) state.code = stored;

    mountSelectors();
    syncSelectors();
    document.documentElement.setAttribute('data-ct-currency', state.code);

    // Wrap prices immediately so switching later is a cheap repaint, not a re-walk.
    scan(document.body);
    ensureNotices();
    observe();

    // Both requests go out together; neither blocks the other.
    var ratesReady = loadRates();

    resolveGeo()
      .then(function (country) {
        applyAudience(country, chosen);
        return ratesReady;
      })
      .then(function () {
        writing = true;
        try { repaint(); ensureNotices(); } finally { writing = false; }
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
